/**
 * query-client.ts — Configuração única do TanStack Query.
 *
 * Escopo da adoção (decisão da migração): as telas de listagem e detalhe usam
 * `useQuery`/`useMutation`; formulários e painéis continuam com o
 * `useEffect + apiFetch` herdado da versão Next.js. Por isso o cache aqui é
 * conservador — nada de refetch agressivo que pudesse divergir do que os
 * formulários mostram.
 */

import { QueryClient } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Dados de RH mudam com pouca frequência dentro de uma sessão.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // 4xx é erro do request, não vale insistir. O 401 já dispara o
        // redirect para /login dentro do apiFetch.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          return false
        }
        return failureCount < 2
      },
    },
    mutations: {
      retry: false,
    },
  },
})

/**
 * Chaves de query centralizadas — evita string solta espalhada pelas páginas
 * e torna a invalidação após mutação previsível.
 */
export const qk = {
  dashboard: ['dashboard'] as const,

  employees: (params?: unknown) => ['employees', params ?? null] as const,
  employee: (id: string) => ['employees', id] as const,

  interns: (params?: unknown) => ['interns', params ?? null] as const,
  intern: (id: string) => ['interns', id] as const,

  exams: (params?: unknown) => ['exams', params ?? null] as const,
  exam: (id: string) => ['exams', id] as const,

  vacations: (params?: unknown) => ['vacations', params ?? null] as const,

  contracts: (params?: unknown) => ['contracts', params ?? null] as const,
  contract: (id: string) => ['contracts', id] as const,
  contractTemplates: (params?: unknown) => ['contract-templates', params ?? null] as const,
  contractTemplate: (id: string) => ['contract-templates', id] as const,

  positions: (params?: unknown) => ['positions', params ?? null] as const,
  benefits: (params?: unknown) => ['benefits', params ?? null] as const,
  bonuses: (params?: unknown) => ['bonuses', params ?? null] as const,
  salaryHistory: (params?: unknown) => ['salary-history', params ?? null] as const,

  notifications: (params?: unknown) => ['notifications', params ?? null] as const,
  unreadCount: ['notifications', 'unread-count'] as const,

  auditLogs: (params?: unknown) => ['audit-logs', params ?? null] as const,
  preRegistrations: (params?: unknown) => ['pre-registrations', params ?? null] as const,
  preRegistration: (id: string) => ['pre-registrations', id] as const,

  calendar: (params?: unknown) => ['calendar', params ?? null] as const,
  reports: (params?: unknown) => ['reports', params ?? null] as const,
}
